import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../config';
import { getSubredditFromUrl, marketingHomeUrl } from '../utils/subreddit';
import {
  formatSubredditPlaceTitle,
  placeTeamPath,
  placeBrowsePath,
  placeTimelapsePath,
} from '../utils/placePaths';

type Row = { username: string; pixels: number };
type FlairLb = { flairCode: string; flairAbbreviated: string; topUsers: Row[] };
type TeamRow = { flairCode: string; flairAbbreviated: string; pixels: number };

export default function PlaceLeaderboardPage() {
  const { subreddit: subParam } = useParams<{ subreddit?: string }>();
  const subreddit = (subParam || getSubredditFromUrl() || 'cfb').toLowerCase();
  const titleSub = formatSubredditPlaceTitle(subreddit);

  const [overall, setOverall] = useState<Row[]>([]);
  const [byFlair, setByFlair] = useState<FlairLb[]>([]);
  const [totalPixelsEdited, setTotalPixelsEdited] = useState<number | null>(null);
  const [teamsByPixels, setTeamsByPixels] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/place/${subreddit}/leaderboard`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        if (!cancelled) {
          setOverall(data.overall || []);
          setByFlair(data.byFlair || []);
          setTotalPixelsEdited(
            typeof data.totalPixelsEdited === 'number' ? data.totalPixelsEdited : null
          );
          setTeamsByPixels(Array.isArray(data.teamsByPixels) ? data.teamsByPixels : []);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subreddit]);

  const backPath = subreddit === 'cfb' && getSubredditFromUrl() ? '/' : `/${subreddit}`;
  const totalDisplay =
    totalPixelsEdited != null ? totalPixelsEdited.toLocaleString() : '—';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', pb: 4 }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar variant="dense">
          <IconButton edge="start" component={RouterLink} to={backPath} size="small" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, flex: 1, fontWeight: 600 }}>
            /r/{titleSub} Place — leaderboard
          </Typography>
          <Button size="small" component={RouterLink} to={placeBrowsePath(subreddit)} sx={{ flexShrink: 0 }}>
            Browse
          </Button>
          <Button size="small" component={RouterLink} to={placeTimelapsePath(subreddit)} sx={{ flexShrink: 0 }}>
            Timelapse
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, pt: 2 }}>
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
          Pixels placed (one count per pixel change).{' '}
          <RouterLink to={placeBrowsePath(subreddit)}>Browse all teams</RouterLink>
          {' · '}
          <Link href={marketingHomeUrl()} color="primary" underline="hover">
            FlairWizard home
          </Link>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper
              elevation={2}
              sx={{
                mb: 3,
                p: 3,
                textAlign: 'center',
                bgcolor: 'primary.dark',
                color: 'primary.contrastText',
              }}
            >
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                Total Pixels Edited
              </Typography>
              <Typography variant="h3" component="p" fontWeight={800} sx={{ mt: 0.5, letterSpacing: 0.5 }}>
                {totalDisplay}
              </Typography>
            </Paper>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Teams (most edited)" />
              <Tab label="Users" />
            </Tabs>

            {tab === 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={56}>#</TableCell>
                      <TableCell>Team</TableCell>
                      <TableCell align="right">Pixels edited</TableCell>
                      <TableCell width={100} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamsByPixels.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No per-team stats yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      teamsByPixels.map((row, i) => (
                        <TableRow key={row.flairCode}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{row.flairAbbreviated}</TableCell>
                          <TableCell align="right">{row.pixels.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Link
                              component={RouterLink}
                              to={placeTeamPath(subreddit, row.flairCode)}
                              variant="caption"
                              underline="hover"
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tab === 1 && (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  All teams combined
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={56}>#</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell align="right">Pixels</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overall.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body2" color="text.secondary">
                              No pixels recorded yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        overall.map((row, i) => (
                          <TableRow key={row.username}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>u/{row.username}</TableCell>
                            <TableCell align="right">{row.pixels.toLocaleString()}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Per team (top contributors)
                </Typography>
                {byFlair.map((block) => (
                  <Accordion key={block.flairCode} disableGutters sx={{ mb: 0.5 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={600} sx={{ flex: 1 }}>
                        {block.flairAbbreviated}
                      </Typography>
                      <Typography
                        component={RouterLink}
                        to={placeTeamPath(subreddit, block.flairCode)}
                        variant="caption"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ mr: 1, color: 'primary.main' }}
                      >
                        Open canvas
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {block.topUsers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No pixels recorded for this canvas yet.
                        </Typography>
                      ) : (
                        <Table size="small">
                          <TableBody>
                            {block.topUsers.map((row, i) => (
                              <TableRow key={row.username}>
                                <TableCell width={40}>{i + 1}</TableCell>
                                <TableCell>u/{row.username}</TableCell>
                                <TableCell align="right">{row.pixels.toLocaleString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
